import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight, Menu, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'

import { company, contactHref, navigation, siteConfig } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const update = () => setScrolled(window.scrollY > 8)

        update()
        window.addEventListener('scroll', update, { passive: true })

        return () => window.removeEventListener('scroll', update)
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        const media = window.matchMedia('(min-width: 1024px)')
        const closeOnDesktop = () => {
            if (media.matches) setMenuOpen(false)
        }

        media.addEventListener('change', closeOnDesktop)

        return () => media.removeEventListener('change', closeOnDesktop)
    }, [])

    useEffect(() => {
        if (!menuOpen) return

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        const previousOverflow = document.body.style.overflow

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', closeOnEscape)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [menuOpen])

    const isActive = (href: string): boolean => {
        if (href === '/') return router.pathname === '/'

        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 bg-background/95 backdrop-blur-md transition-shadow duration-300',
                scrolled || menuOpen ? 'shadow-[0_1px_0_0_var(--border),0_8px_24px_-12px_rgb(20_24_60/0.18)]' : 'shadow-[0_1px_0_0_var(--border)]'
            )}
        >
            <a
                href='#main'
                className='sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-[60] focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-foreground focus:shadow-lg'
            >
                Preskoči na sadržaj
            </a>

            <Container className='flex h-[4.5rem] items-center justify-between gap-6'>
                <Link
                    href='/'
                    aria-label={`${siteConfig.name}, naslovna`}
                    className='rounded-md outline-none focus-visible:ring-3 focus-visible:ring-ring/50'
                >
                    <Logo />
                </Link>

                <nav aria-label='Glavna navigacija' className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {navigation.map(item => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={isActive(item.href) ? 'page' : undefined}
                                    className={cn(
                                        'relative block rounded-md px-3.5 py-2 text-xs font-semibold tracking-[0.12em] text-foreground/75 uppercase transition-colors outline-none hover:text-primary focus-visible:ring-3 focus-visible:ring-ring/50 aria-[current=page]:text-primary',
                                        'after:absolute after:inset-x-3.5 after:bottom-0.5 after:h-0.5 after:origin-left after:scale-x-0 after:bg-primary after:transition-transform hover:after:scale-x-100 aria-[current=page]:after:scale-x-100'
                                    )}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='hidden items-center gap-6 lg:flex'>
                    {company.phone && (
                        <a href={company.phoneHref} className='flex items-center gap-2 text-sm font-semibold text-foreground transition-colors hover:text-primary'>
                            <Phone className='size-4 text-primary' aria-hidden='true' />
                            {company.phone}
                        </a>
                    )}

                    <Link href={contactHref} className={buttonVariants({ size: 'lg' })}>
                        Pošaljite upit
                        <ArrowRight data-icon='inline-end' aria-hidden='true' />
                    </Link>
                </div>

                <button
                    type='button'
                    aria-expanded={menuOpen}
                    aria-controls='mobile-menu'
                    aria-label={menuOpen ? 'Zatvori meni' : 'Otvori meni'}
                    onClick={() => setMenuOpen(open => !open)}
                    className='inline-flex size-11 items-center justify-center rounded-md border border-border text-foreground transition-colors outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 lg:hidden'
                >
                    {menuOpen ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                </button>
            </Container>

            {menuOpen && (
                <div
                    id='mobile-menu'
                    className='h-[calc(100dvh-4.5rem)] overflow-y-auto border-t border-border bg-background lg:hidden'
                >
                    <Container className='flex flex-col gap-10 py-6'>
                        <nav aria-label='Mobilna navigacija'>
                            <ul className='flex flex-col divide-y divide-border'>
                                {navigation.map(item => (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            onClick={() => setMenuOpen(false)}
                                            aria-current={isActive(item.href) ? 'page' : undefined}
                                            className='flex items-center justify-between py-4 font-heading text-2xl font-bold tracking-tight outline-none focus-visible:text-primary aria-[current=page]:text-primary'
                                        >
                                            {item.label}
                                            <ArrowRight className='size-5 text-primary' aria-hidden='true' />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className='flex flex-col gap-3'>
                            <Link
                                href={contactHref}
                                onClick={() => setMenuOpen(false)}
                                className={buttonVariants({ size: 'xl' })}
                            >
                                Pošaljite upit
                                <ArrowRight data-icon='inline-end' aria-hidden='true' />
                            </Link>

                            {company.phone && (
                                <a href={company.phoneHref} className={buttonVariants({ size: 'xl', variant: 'outline' })}>
                                    <Phone data-icon='inline-start' aria-hidden='true' />
                                    {company.phone}
                                </a>
                            )}
                        </div>

                        <p className='text-sm text-muted-foreground'>
                            {company.location}
                        </p>
                    </Container>
                </div>
            )}
        </header>
    )
}
